import Link from 'next/link';
import React from 'react';

import { useAuth } from '@/provider/UserProvider';
import type { UserProfile } from '@/types/user';
import { getDurationAsTimeFromNow } from '@/utils';

import AvatarPlaceholder from '../../../components/avatar-default';
import NsfwMark from '@/components/NsfwMark';
import { Images } from '@/components';
import PromptChat from '@/app/chat/group-chat/PromptChat';

export interface Mention {
  id: number;
  isPartner: boolean;
  picture: string;
  publicAddress: string;
  username: string;
  verify: boolean;
}
export interface IDataMessage {
  content: string;
  createdAt: string;
  provider: UserProfile;
  creator?: number;
  id?: number;
  processingAsset?: any;
  metadata?: {
    title: string;
    description: string;
    image: string;
  };
  mentions: Mention[];
}
export type Part = { type: 'link' | 'text' | 'mention'; value: string; publicAddress?: string };

const ItemChat: React.FC<{
  message: IDataMessage;
  nextItem: IDataMessage;
  prevItem: IDataMessage;
}> = ({ message, nextItem, prevItem }) => {
  const dateItem = new Date(message.createdAt).setSeconds(0).toString();
  const dateNextItem = new Date(nextItem?.createdAt).setSeconds(0).toString();
  const datePrevItem = new Date(prevItem?.createdAt).setSeconds(0).toString();
  const { auth } = useAuth();

  function isValidHttpUrl(content: string) {
    let url;
    try {
      url = new URL(content);
    } catch (_) {
      return false;
    }
    return url.protocol === 'http:' || url.protocol === 'https:';
  }

  const link = message.content.split(' ').find(isValidHttpUrl);

  const parseContent = (content: string, mentions: Mention[]): Part[] => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const mentionRegex = /<@(\d+)>/g;

    // Map user IDs to Mention objects for quick lookup
    const mentionMap: { [key: number]: Mention } = {};
    mentions.forEach((mention) => {
      mentionMap[mention?.id] = mention;
    });

    const parts: Part[] = content.split(urlRegex).flatMap((part: any) => {
      if (part.match(urlRegex)) {
        return [{ type: 'link', value: part }];
      }

      return part.split(mentionRegex).map((subPart: any, index: any) => {
        if (index % 2 === 1) {
          const userId = parseInt(subPart, 10);
          const mention = mentionMap[userId];
          return mention
            ? {
                type: 'mention',
                value: `@${mention?.username}`,
                publicAddress: mention?.publicAddress,
              }
            : { type: 'text', value: `<@${userId}>` };
        }
        return { type: 'text', value: subPart };
      });
    });

    return parts;
  };

  const parsedContent = parseContent(message.content, message?.mentions);

  const isTheBelongMessage: boolean =
    (nextItem?.creator === message.creator && dateItem !== dateNextItem) ||
    (prevItem?.creator === message.creator && dateItem !== datePrevItem) ||
    prevItem?.creator !== message.creator ||
    nextItem?.creator !== message.creator;

  return (
    <div className={`${!isTheBelongMessage ? 'mb-1' : 'mb-4'} pl-1`}>
      {auth.user.id === message.creator ? (
        <div className="flex flex-col items-end">
          <div className="w-fit max-w-[70%]" style={{ wordBreak: 'break-word' }}>
            <div className="rounded-[8px] bg-orange-500 p-3 md:py-2">
              <span className="text-[14px] text-white [overflow-wrap:anywhere]">
                {parsedContent.map((part, index) =>
                  part.type !== 'text' ? (
                    part.type === 'link' ? (
                      <a
                        key={index}
                        target="_blank"
                        href={part.value}
                        className="text-white underline"
                      >
                        {part.value}
                      </a>
                    ) : (
                      <span
                        key={index}
                        className="cursor-pointer font-semibold text-white"
                        onClick={() => window.open(`/profile/${part?.publicAddress}`, '_blank')}
                      >
                        {part.value}
                      </span>
                    )
                  ) : (
                    <PromptChat key={index} index={index} message={message} part={part} />
                  ),
                )}
              </span>
              {message?.processingAsset && (
                <div className="mt-2 rounded-[6px] bg-white p-4 shadow-md lg:p-2">
                  {message?.processingAsset?.isNsfw && (
                    <NsfwMark
                      absolute={false}
                      className="h-[195px] !w-[195px]"
                      url={message?.processingAsset?.url}
                    />
                  )}
                  {message?.processingAsset?.url && !message?.processingAsset?.isNsfw && (
                    <Images
                      src={message?.processingAsset?.url}
                      alt={message?.processingAsset?.url}
                      className="h-48 w-full rounded-t-lg object-cover lg:h-32 cursor-pointer"
                      zoom
                    />
                  )}
                </div>
              )}
              {message?.metadata?.title && (
                <Link target="_blank" href={link || ''}>
                  <div className="mt-2 rounded-[6px] bg-white p-4 shadow-md lg:p-2">
                    {message.metadata.image && (
                      <img
                        src={message.metadata.image}
                        alt={message.metadata.title}
                        className="h-48 w-full rounded-t-lg object-cover lg:h-32"
                      />
                    )}
                    <div className="pt-2 lg:pt-1">
                      <h3 className="text-[14px] font-semibold">{message.metadata.title}</h3>
                      <p className="text-[12px] text-gray-600 lg:hidden">
                        {message.metadata.description}
                      </p>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          </div>
          {isTheBelongMessage && (
            <span className="text-[12px] text-gray-500">
              {getDurationAsTimeFromNow(message?.createdAt)}
            </span>
          )}
        </div>
      ) : (
        <div className="w-fit max-w-[60%] break-words" style={{ wordBreak: 'break-word' }}>
          <div className="flex w-fit max-w-full flex-row">
            {isTheBelongMessage ? (
              <div className="self-end rounded-[100%]">
                <Link
                  href={`/profile/${message.provider.publicAddress}`}
                  className="flex h-full w-full items-center"
                >
                  <div className="h-[48px] relative w-[48px] rounded-[50%] md:h-[32px] md:w-[32px]">
                    <AvatarPlaceholder
                      src={message.provider?.picture}
                      address={message.provider?.publicAddress}
                      className="h-full w-full rounded-[50%] object-cover"
                      decor={message.provider?.avatarDecoration}
                      decorScale={25}
                    />
                  </div>
                </Link>
              </div>
            ) : (
              <div className="self-end rounded-[100%]">
                <div className="flex h-full w-full items-center">
                  <div className="h-[48px] w-[48px] rounded-[50%] md:h-[32px] md:w-[32px]" />
                </div>
              </div>
            )}
            <div className="ml-4 rounded-[8px] bg-gray-100 p-3 md:ml-2 md:p-2">
              <span className="mt-1 text-[14px] text-gray-700 [overflow-wrap:anywhere]">
                {parsedContent.map((part, index) =>
                  part.type !== 'text' ? (
                    part.type === 'link' ? (
                      <a
                        key={index}
                        target="_blank"
                        href={part.value}
                        className="text-gray-700 underline md:overflow-hidden md:text-ellipsis"
                      >
                        {part.value}
                      </a>
                    ) : (
                      <span
                        key={index}
                        className="cursor-pointer font-semibold text-orange-500"
                        onClick={() => window.open(`/profile/${part?.publicAddress}`, '_blank')}
                      >
                        {part.value}
                      </span>
                    )
                  ) : (
                    <PromptChat key={index} index={index} message={message} part={part} />
                  ),
                )}
              </span>
              {message?.processingAsset && (
                <div className="mt-2 rounded-[6px] bg-white p-4 shadow-md lg:p-2">
                  {message?.processingAsset?.isNsfw && (
                    <NsfwMark
                      absolute={false}
                      className="h-[195px] !w-[195px]"
                      url={message?.processingAsset?.url}
                    />
                  )}
                  {message?.processingAsset?.url && !message?.processingAsset?.isNsfw && (
                    <Images
                      src={message?.processingAsset?.url}
                      alt={message?.processingAsset?.url}
                      className="h-48 w-full rounded-t-lg object-cover lg:h-32 cursor-pointer"
                      zoom
                    />
                  )}
                </div>
              )}
              {message?.metadata?.title && (
                <Link target="_blank" href={link || ''}>
                  <div className="mt-2 rounded-[6px] bg-white p-4 shadow-md lg:p-2">
                    {message.metadata.image && (
                      <img
                        src={message.metadata.image}
                        alt={message.metadata.title}
                        className="h-48 w-full rounded-t-lg object-cover lg:h-32"
                      />
                    )}
                    <div className="pt-2 lg:pt-1">
                      <h3 className="text-[14px] font-semibold">{message.metadata.title}</h3>
                      <p className="text-[12px] text-gray-600 lg:hidden">
                        {message.metadata.description}
                      </p>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          </div>
          {isTheBelongMessage ? (
            <p className="flex pl-[64px] text-[12px] text-gray-500 md:pl-[40px]">
              {getDurationAsTimeFromNow(message?.createdAt)}
              <span className="pl-[2px]">
                by
                <Link
                  className="max-w-[100px] truncate pl-[2px] text-[12px] font-semibold text-gray-600 md:hidden"
                  href={`/profile/${message.provider.publicAddress}`}
                >
                  {message.provider?.username}
                </Link>
              </span>
            </p>
          ) : (
            <></>
          )}
        </div>
      )}
    </div>
  );
};

export default ItemChat;
